/**
 * ModelSelector — LLM provider/model dropdown for @guardrail.tech admins.
 *
 * Only renders when the user has the `model_selection` realm role.
 * Fetches available providers from the API, shows current selection,
 * and persists changes to Keycloak user attributes.
 */

import { useState, useEffect } from 'react';
import { Settings, ChevronDown, Check, Cpu } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface LLMProvider {
  id: string;
  name: string;
  description: string;
  models: string[];
  configured: boolean;
}

interface ModelConfig {
  providers: LLMProvider[];
  current: {
    provider: string;
    model: string;
  };
}

export function ModelSelector() {
  const { user, token } = useAuth();
  const [config, setConfig] = useState<ModelConfig | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only show for users with model_selection role
  const hasPermission = user?.roles?.includes('model_selection') &&
    user?.email?.endsWith('@guardrail.tech');

  useEffect(() => {
    if (!hasPermission || !token) return;

    fetch(`${import.meta.env.VITE_API_URL}/v2/model-config`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.providers) setConfig(data);
      })
      .catch(() => {/* silent */});
  }, [hasPermission, token]);

  if (!hasPermission || !config) return null;

  const currentProvider = config.providers.find((p) => p.id === config.current.provider);
  const currentLabel = config.current.provider === 'auto'
    ? 'Auto-detect'
    : currentProvider?.name || config.current.provider;

  const handleSelect = async (providerId: string, model?: string) => {
    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/v2/model-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ provider: providerId, model: model || '' }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.message || 'Failed to update');
        return;
      }

      setConfig((prev) =>
        prev ? { ...prev, current: { provider: providerId, model: model || '' } } : prev
      );
      setIsOpen(false);
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-700 bg-zinc-800/50 hover:bg-zinc-700/50 transition text-sm text-zinc-300 hover:text-white"
        title="AI Model Selection"
        aria-label="Select AI model"
        aria-expanded={isOpen}
      >
        <Cpu className="w-3.5 h-3.5 text-emerald-400" />
        <span className="hidden sm:inline">{currentLabel}</span>
        {config.current.model && (
          <span className="text-zinc-500 text-xs hidden md:inline">
            ({config.current.model})
          </span>
        )}
        <ChevronDown className={`w-3 h-3 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl z-50 overflow-hidden">
            <div className="p-3 border-b border-zinc-800">
              <div className="flex items-center gap-2 text-sm font-medium text-white">
                <Settings className="w-4 h-4 text-emerald-400" />
                AI Model Provider
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Select the LLM provider for AI-enhanced scanning
              </p>
            </div>

            <div className="p-2 max-h-96 overflow-y-auto">
              {/* Auto option */}
              <button
                onClick={() => handleSelect('auto')}
                className={`w-full text-left p-3 rounded-lg mb-1 transition ${
                  config.current.provider === 'auto'
                    ? 'bg-emerald-500/10 border border-emerald-500/30'
                    : 'hover:bg-zinc-800 border border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white">Auto-detect</span>
                  {config.current.provider === 'auto' && (
                    <Check className="w-4 h-4 text-emerald-400" />
                  )}
                </div>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Automatically select the best available provider
                </p>
              </button>

              {/* Provider options */}
              {config.providers.map((provider) => (
                <div key={provider.id} className="mb-1">
                  <button
                    onClick={() => handleSelect(provider.id, provider.models[0])}
                    disabled={!provider.configured}
                    className={`w-full text-left p-3 rounded-lg transition ${
                      config.current.provider === provider.id
                        ? 'bg-emerald-500/10 border border-emerald-500/30'
                        : provider.configured
                        ? 'hover:bg-zinc-800 border border-transparent'
                        : 'opacity-40 cursor-not-allowed border border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-white">{provider.name}</span>
                      <div className="flex items-center gap-2">
                        {!provider.configured && (
                          <span className="text-[10px] text-zinc-600 bg-zinc-800 px-1.5 py-0.5 rounded">
                            Not configured
                          </span>
                        )}
                        {config.current.provider === provider.id && (
                          <Check className="w-4 h-4 text-emerald-400" />
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5">{provider.description}</p>
                    {provider.configured && config.current.provider === provider.id && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {provider.models.map((model) => (
                          <button
                            key={model}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelect(provider.id, model);
                            }}
                            className={`text-[11px] px-2 py-0.5 rounded border transition ${
                              config.current.model === model
                                ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300'
                                : 'border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                            }`}
                          >
                            {model}
                          </button>
                        ))}
                      </div>
                    )}
                  </button>
                </div>
              ))}
            </div>

            {error && (
              <div className="p-2 border-t border-zinc-800">
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}

            {saving && (
              <div className="p-2 border-t border-zinc-800">
                <p className="text-xs text-zinc-500">Saving...</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
